import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  acceptGroup,
  answerGroupAsk,
  befriendInGroup,
  cancelBefriendInGroup,
  cancelGroupInvite,
  declineGroup,
  friendSearch,
  getGroup,
  getGroups,
  getProjects,
  inviteToGroup,
  leaveGroup,
  openGroupChat,
  openGroupDm,
  removeFromGroup,
  resetGroupLink,
  setGroupIcon,
  updateGroup,
  type FriendAccount,
  type GroupPerson,
  type GroupRow,
  type GroupView,
} from '../transport/api'
import { Icon } from '../ui/Icon'
import { Sheet } from '../ui/Sheet'
import { Avatar } from '../chat/Avatar'
import { Markdown } from '../chat/Markdown'
import { groupErrorText } from './groupText'
import { GroupFace } from './GroupFace'
import { groupLinkUrl } from './groupLink'
import { copyText } from '../ui/clipboard'
import { listConversations } from '../chatv1/transport'
import { avatarFromFile } from '../auth/profileImage'
import '../friends/friends.css'
import './groups.css'
import { EditLimits, GroupLimitsRow } from './GroupLimits'

/**
 * GROUPS, IN HUMANS — owner, 2026-10-18: group 的欄位可以放在 SidePanel Humans 的那一個頁面裡面, 這樣子使用者
 * 就非常清楚的知道他是在與人類相關的活動 (it first sat in the main menu: 這一個 Group 它的顯示區域不會在 Side
 * Panel 而是在主選單設定選單裡面 — moved the same day). Under the friends, in the friends' own row style.
 *
 * The invitations waiting on you come first (Join / Decline — an invitation is yours to accept: joining
 * shows you to every member), then your groups, the ones you created first. A row opens the group's
 * sheet: its Members button — the list in a searchable popup, Add friend beside each who is not one yet —
 * its rules, and, for its owner, Invite, the requests to join, the invitations still out, hidden or
 * searchable, and Remove beside each member.
 *
 * 加入群組並不等於加入好友: nothing here is a friendship, and only friends share agents. A member is
 * shown by the name and picture they chose; their ADDRESS only where you already have it (yours, a
 * friend's, or — as owner — the people you invited or let in).
 *
 * Each group is a block (owner, 2026-10-18): its picture opens the sheet (Group 的主要的 icon 可以彈跳出來
 * 設定頁面), its name opens the group's chat (Group 本身可以呈現出來一個 Group 的討論串 … Group Chat), and its
 * members are listed under it — tapping one opens the same DM a friend's row opens (只要使用者點擊其他的使用者,
 * 就可以開啟對話以及聊天視窗).
 */
export function GroupsSection({
  openGid,
  onOpened,
  onOpenDm,
  onOpenChat,
}: {
  openGid?: string | null
  onOpened?: () => void
  /** A member's DM: by a friend's address, or by the dm's conversation id (urlState.isDmConv). */
  onOpenDm?: (emailOrConv: string) => void
  /** The group's chat room, by its conversation id. */
  onOpenChat?: (convId: string) => void
}) {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['groups'], queryFn: getGroups })
  const [open, setOpen] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  // ＋ → Group just made one: open it here, where its owner invites people.
  useEffect(() => {
    if (!openGid) return
    setOpen(openGid)
    onOpened?.()
  }, [openGid, onOpened])
  const answer = useMutation({
    mutationFn: ({ gid, yes }: { gid: string; yes: boolean }) => (yes ? acceptGroup(gid) : declineGroup(gid)),
    onSuccess: (_r, v) => {
      void qc.invalidateQueries({ queryKey: ['groups'] })
      if (v.yes) setOpen(v.gid)
    },
    onError: (e) => setErr(groupErrorText(e)),
  })
  const groups = q.data?.groups ?? []
  const invitations = q.data?.invitations ?? []
  return (
    <div className="fr-list fr-groups">
      <div className="fr-head">Groups{groups.length ? ` · ${groups.length}` : ''}</div>
      {q.isPending && <div className="fr-empty">loading…</div>}
      {q.isError && <div className="fr-empty fr-err">couldn’t load groups — {groupErrorText(q.error)}</div>}
      {!q.isPending && !q.isError && groups.length === 0 && invitations.length === 0 && (
        <div className="fr-empty">No groups yet — start one from the ＋ button.</div>
      )}
      {invitations.map((i) => (
        <div className="fr-card grp-invite" key={`inv-${i.gid}`}>
          <GroupFace icon={i.icon} />
          <span className="fr-meta">
            <span className="fr-name">{i.name}</span>
            <span className="fr-sub">{i.by ? `${i.by} invited you` : 'You are invited'}</span>
          </span>
          <span className="fr-btns">
            <button
              type="button"
              className="fr-btn ok"
              disabled={answer.isPending}
              onClick={() => answer.mutate({ gid: i.gid, yes: true })}
            >
              Join
            </button>
            <button
              type="button"
              className="fr-btn"
              disabled={answer.isPending}
              onClick={() => answer.mutate({ gid: i.gid, yes: false })}
            >
              Decline
            </button>
          </span>
        </div>
      ))}
      {groups.map((g) => (
        <GroupBlock
          key={g.gid}
          g={g}
          onSettings={() => setOpen(g.gid)}
          {...(onOpenDm ? { onOpenDm } : {})}
          {...(onOpenChat ? { onOpenChat } : {})}
          onError={setErr}
        />
      ))}
      {err && <div className="fr-empty fr-err">{err}</div>}
      {open && <GroupSheet key={open} gid={open} onClose={() => setOpen(null)} />}
    </div>
  )
}

/**
 * One group in Humans: the picture (→ its sheet), the name (→ its chat, with the chat's unread count), and
 * the other members under it, each a door to their DM.
 */
function GroupBlock({
  g,
  onSettings,
  onOpenDm,
  onOpenChat,
  onError,
}: {
  g: GroupRow
  onSettings: () => void
  onOpenDm?: (emailOrConv: string) => void
  onOpenChat?: (convId: string) => void
  onError: (text: string | null) => void
}) {
  const qc = useQueryClient()
  const view = useQuery({ queryKey: ['group', g.gid], queryFn: () => getGroup(g.gid) })
  // The room rides the projects list (it is a project room that names its group); the rail's Projects skip it.
  const projects = useQuery({ queryKey: ['projects'], queryFn: getProjects })
  const room = projects.data?.projects.find((p) => p.group_id === g.gid)
  const chat = useMutation({
    mutationFn: () => openGroupChat(g.gid),
    onSuccess: async (r) => {
      // A room made just now is not in the list yet: read it again before opening it.
      await qc.invalidateQueries({ queryKey: ['projects'] })
      onOpenChat?.(r.conv_id)
    },
    onError: (e) => onError(groupErrorText(e)),
  })
  const openChat = () => {
    onError(null)
    if (room) {
      onOpenChat?.(room.project_id)
      // Opening also brings the room along to the group (the BFF's repair for a change it missed).
      void openGroupChat(g.gid).catch(() => undefined)
    } else chat.mutate()
  }
  /*
   * A MEMBER'S DM — owner, 2026-10-18: 只有加好友之後 Email 才會互相顯示. A friend's opens by their address, as
   * from Friends; anyone else's the BFF opens by their handle and hands back its id, which opens it the same
   * way (urlState.isDmConv). Its unread comes from the app's dm list by that id (`m.dm`), so no row needs an
   * address to show one.
   */
  const dms = useQuery({ queryKey: ['dm-conversations'], queryFn: () => listConversations('dm') })
  const dm = useMutation({
    mutationFn: (member: string) => openGroupDm(g.gid, member),
    onSuccess: async (r) => {
      // A dm made just now is not in the list yet: read it again so it opens named.
      await qc.invalidateQueries({ queryKey: ['dm-conversations'] })
      onOpenDm?.(r.conv_id)
    },
    onError: (e) => onError(groupErrorText(e)),
  })
  const mates = (view.data?.members ?? []).filter((m) => !m.you)
  const unread = room?.unread ?? 0
  return (
    <div className="grp-block">
      <div className="grp-line">
        <button
          type="button"
          className="grp-face-btn"
          aria-label={`${g.name} settings`}
          title="Group settings"
          onClick={onSettings}
        >
          <GroupFace icon={g.icon} />
          {g.asks > 0 && (
            <span className="grp-badge grp-face-badge" title={`${g.asks} waiting to join`}>
              {g.asks}
            </span>
          )}
        </button>
        <button
          type="button"
          className="fr-friend grp-row"
          aria-label={`Open ${g.name} chat`}
          disabled={chat.isPending}
          onClick={openChat}
        >
          <span className="fr-meta">
            <span className="fr-name">{g.name}</span>
            <span className="fr-sub">
              {[
                chat.isPending ? 'Opening the chat…' : g.role === 'owner' ? 'Created by you' : 'Joined',
                `${g.members} ${g.members === 1 ? 'member' : 'members'}`,
                g.visibility === 'searchable' ? 'Searchable' : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </span>
          {unread > 0 && <span className="fr-unread">{unread > 99 ? '99+' : unread}</span>}
          <Icon name="chat" size={16} className="grp-go" />
        </button>
      </div>
      {mates.length > 0 && (
        <div className="grp-mates">
          {mates.map((m) => {
            const name = m.name ?? m.email ?? 'Someone'
            const face = (
              <Avatar picture={m.picture ?? null} label={name} email={m.email ?? null} className="fr-pic-sm" />
            )
            // The chip sits INSIDE the name's line: .fr-rowname is a column, where it would become a full-width bar.
            const label = (
              <span className="fr-meta">
                <span className="fr-name">
                  {name}
                  {m.role === 'owner' && <span className="grp-chip">Owner</span>}
                </span>
              </span>
            )
            if (!onOpenDm) {
              return (
                <div className="fr-friend grp-mate" key={m.member}>
                  {face}
                  {label}
                </div>
              )
            }
            const n = (m.dm && dms.data?.conversations.find((c) => c.conv_id === m.dm)?.unread) || 0
            return (
              <button
                type="button"
                className="fr-friend grp-mate"
                key={m.member}
                aria-label={`Message ${name}`}
                title={`Message ${name}`}
                disabled={dm.isPending}
                onClick={() => (m.email ? onOpenDm(m.email) : dm.mutate(m.member))}
              >
                {face}
                {label}
                {n > 0 && <span className="fr-unread">{n > 99 ? '99+' : n}</span>}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

type View = 'main' | 'invite' | 'leave' | 'links' | 'limits'

function GroupSheet({ gid, onClose }: { gid: string; onClose: () => void }) {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['group', gid], queryFn: () => getGroup(gid) })
  const [view, setView] = useState<View>('main')
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // The rules' popup, reading or (the owner) writing; the draft outlives it, so a stray tap outside does not lose it.
  const [rules, setRules] = useState<RulesMode | null>(null)
  const [draft, setDraft] = useState<string | null>(null)
  // The member list is its own popup — owner, 2026-10-18: a group of dozens or hundreds does not fit in this sheet.
  const [membersOpen, setMembersOpen] = useState(false)
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['group', gid] })
    void qc.invalidateQueries({ queryKey: ['groups'] })
  }
  /** One call, its refusal in words, and the lists read again. */
  const run = async (fn: () => Promise<unknown>): Promise<boolean> => {
    setErr(null)
    setBusy(true)
    try {
      await fn()
      refresh()
      return true
    } catch (e) {
      setErr(groupErrorText(e))
      return false
    } finally {
      setBusy(false)
    }
  }
  const g = q.data
  const owner = g?.role === 'owner'
  const back = (label: string) => (
    <button type="button" className="ac-back" aria-label="Back" onClick={() => setView('main')}>
      <Icon name="chevron_left" size={18} />
      {label}
    </button>
  )
  return (
    <>
      <Sheet title={g?.name ?? 'Group'} onClose={onClose} className="mch-sheet grp-sheet">
        {q.isPending && <p className="gh-note">Loading…</p>}
        {q.isError && <p className="gh-note">{groupErrorText(q.error)}</p>}
        {g && view === 'main' && (
          <>
            <GroupPhoto g={g} owner={owner} busy={busy} run={run} />
            <div className="ac-menu grp-menu grp-members-open">
              <button type="button" className="ac-row" onClick={() => setMembersOpen(true)}>
                <Icon name="group" size={18} />
                <span className="ac-row-label">Members</span>
                <span className="ac-row-val">{g.members.length}</span>
                <Icon name="chevron_right" size={18} className="ac-row-go" />
              </button>
            </div>
            <GroupRules g={g} owner={owner} onOpen={setRules} />
            <GroupLimitsRow g={g} owner={owner} onEdit={() => setView('limits')} />
            <SocialLinks g={g} owner={owner} onEdit={() => setView('links')} />
            <ShareLink g={g} owner={owner} busy={busy} run={run} />
            {owner && <OwnerParts g={g} busy={busy} run={run} />}
            <div className="ac-menu grp-menu">
              {owner && (
                <button type="button" className="ac-row" onClick={() => setView('invite')}>
                  <Icon name="add" size={18} />
                  <span className="ac-row-label">Invite people</span>
                  <Icon name="chevron_right" size={18} className="ac-row-go" />
                </button>
              )}
              <button type="button" className="ac-row" onClick={() => setView('leave')}>
                <Icon name="logout" size={18} />
                <span className="ac-row-label">Leave group</span>
                <Icon name="chevron_right" size={18} className="ac-row-go" />
              </button>
            </div>
          </>
        )}
        {g && view === 'invite' && (
          <>
            {back('Invite people')}
            <InvitePeople gid={gid} onInvited={refresh} />
          </>
        )}
        {g && view === 'links' && (
          <>
            {back('Links')}
            <EditLinks
              g={g}
              busy={busy}
              onSave={async (links) => {
                if (await run(() => updateGroup(gid, { links }))) setView('main')
              }}
            />
          </>
        )}
        {g && view === 'limits' && (
          <>
            {back('Posting limits')}
            <EditLimits
              g={g}
              busy={busy}
              onSave={async (limits) => {
                if (await run(() => updateGroup(gid, limits))) setView('main')
              }}
            />
          </>
        )}
        {g && view === 'leave' && (
          <>
            {back('Leave group')}
            <LeaveGroup
              g={g}
              busy={busy}
              onLeave={async (next) => {
                if (await run(() => leaveGroup(gid, next))) onClose()
              }}
            />
          </>
        )}
        {err && <p className="gh-note mch-err">{err}</p>}
      </Sheet>
      {g && membersOpen && (
        <MembersSheet
          g={g}
          owner={owner}
          busy={busy}
          err={err}
          onRemove={(m) => void run(() => removeFromGroup(gid, m))}
          onFriend={(m, yes) => void run(() => (yes ? befriendInGroup(gid, m) : cancelBefriendInGroup(gid, m)))}
          onClose={() => setMembersOpen(false)}
        />
      )}
      {g && rules && (
        <RulesSheet
          g={g}
          owner={owner}
          mode={rules}
          draft={draft ?? g.rules ?? ''}
          busy={busy}
          err={err}
          onMode={setRules}
          onDraft={setDraft}
          onSave={async (text) => {
            if (await run(() => updateGroup(gid, { rules: text }))) {
              setDraft(null)
              setRules(text.trim() ? 'read' : null)
            }
          }}
          onClose={() => setRules(null)}
        />
      )}
    </>
  )
}

/**
 * THE MEMBER LIST, IN ITS OWN POPUP — owner, 2026-10-18: 可以把這個 Members 做成一個按鈕, 讓使用者按下的時候再彈出來
 * Member List. 如果直接放在這個 group 的設定選單, 就顯得太擁擠, 等使用者增加到幾十人或上百人的時候, 這個就放不下了;
 * 彈跳出來的 Member List 也可以使用搜尋的功能來搜尋. Every member, in join order, each with what its row offers
 * (Add friend, Remove for the owner); the search narrows them by name — and by address where one is shown (yours, a
 * friend's), never by one the viewer was not given.
 */
function MembersSheet({
  g,
  owner,
  busy,
  err,
  onRemove,
  onFriend,
  onClose,
}: {
  g: GroupView
  owner: boolean
  busy: boolean
  err: string | null
  onRemove: (member: string) => void
  onFriend: (member: string, yes: boolean) => void
  onClose: () => void
}) {
  const [query, setQuery] = useState('')
  const needle = query.trim().toLocaleLowerCase()
  const all = g.members
  const shown = needle
    ? all.filter((m) => [m.name, m.email].some((v) => !!v && v.toLocaleLowerCase().includes(needle)))
    : all
  return (
    <Sheet title="Members" onClose={onClose} className="mch-sheet grp-sheet grp-members-sheet">
      <div className="grp-members-find">
        <span className="grp-search">
          <Icon name="search" size={16} />
          <input
            className="nas-input"
            type="search"
            placeholder="Search members"
            aria-label="Search members"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </span>
        <div className="gh-sec grp-h">
          {needle
            ? `${shown.length} of ${all.length}`
            : `${all.length} ${all.length === 1 ? 'member' : 'members'}`}
        </div>
      </div>
      {shown.map((m) => (
        <MemberRow
          key={m.member}
          person={m}
          canRemove={owner && !m.you}
          busy={busy}
          onRemove={() => onRemove(m.member)}
          onFriend={(yes) => onFriend(m.member, yes)}
        />
      ))}
      {needle && !shown.length && <p className="gh-note">No member matches “{query.trim()}”.</p>}
      {err && <p className="gh-note mch-err">{err}</p>}
    </Sheet>
  )
}

function PersonFace({ person, size = 28 }: { person: GroupPerson; size?: number }) {
  const label = person.name ?? person.email ?? 'Someone'
  return (
    <Avatar
      picture={person.picture}
      label={label}
      email={person.email ?? null}
      className={`grp-face${size > 28 ? ' big' : ''}`}
    />
  )
}

function MemberRow({
  person,
  canRemove,
  busy,
  onRemove,
  onFriend,
}: {
  person: GroupPerson
  canRemove: boolean
  busy: boolean
  onRemove: () => void
  /** Add friend (true) or take the request back (false) — owner, 2026-10-18: 加上 Add friend 按鈕. */
  onFriend: (yes: boolean) => void
}) {
  const [armed, setArmed] = useState(false)
  const name = person.name ?? person.email ?? 'Someone'
  const rel = person.relationship
  return (
    <div className="grp-person">
      <PersonFace person={person} />
      <span className="mch-meta">
        <span className="mch-name">
          {person.you ? `${name} (you)` : name}
          {person.role === 'owner' && <span className="grp-chip">Owner</span>}
        </span>
        {person.email && !person.you && <span className="grp-sub">{person.email}</span>}
      </span>
      {/* Only friends share agents (owner, 2026-10-18), so the way from a group to that is here. */}
      {!person.you && !armed && rel === 'none' && (
        <button type="button" className="fr-btn ok" disabled={busy} onClick={() => onFriend(true)}>
          Add friend
        </button>
      )}
      {!person.you && !armed && rel === 'outgoing' && (
        <button type="button" className="fr-btn" disabled={busy} onClick={() => onFriend(false)}>
          Requested · cancel
        </button>
      )}
      {!person.you && !armed && rel === 'incoming' && (
        <button type="button" className="fr-btn ok" disabled={busy} onClick={() => onFriend(true)}>
          Accept friend
        </button>
      )}
      {!person.you && !armed && rel === 'friends' && <span className="fr-state friends">Friends ✓</span>}
      {canRemove &&
        (armed ? (
          <span className="fr-btns">
            <button type="button" className="fr-btn danger" disabled={busy} onClick={onRemove}>
              Remove {name}?
            </button>
            <button type="button" className="fr-btn" onClick={() => setArmed(false)}>
              Cancel
            </button>
          </span>
        ) : (
          <button type="button" className="fr-btn" disabled={busy} onClick={() => setArmed(true)}>
            Remove
          </button>
        ))}
    </div>
  )
}

/**
 * THE GROUP'S PICTURE — owner, 2026-10-18: Group Owner 也可以上傳一個照片用來標示 Group 的顯示的 icon. The owner
 * picks a photo (resized here the way a profile picture is: auth/profileImage avatarFromFile) or removes it;
 * every list draws it (GroupFace).
 */
function GroupPhoto({
  g,
  owner,
  busy,
  run,
}: {
  g: GroupView
  owner: boolean
  busy: boolean
  run: (fn: () => Promise<unknown>) => Promise<boolean>
}) {
  const [err, setErr] = useState<string | null>(null)
  return (
    <div className="grp-photo">
      <GroupFace icon={g.icon} big />
      {owner && (
        <span className="fr-btns">
          <label className={`fr-btn grp-photo-pick${busy ? ' off' : ''}`}>
            {g.icon ? 'Change photo' : 'Add a photo'}
            <input
              type="file"
              accept="image/*"
              disabled={busy}
              aria-label={g.icon ? 'Change the group’s photo' : 'Add a photo for the group'}
              onChange={async (e) => {
                const file = e.target.files?.[0]
                e.target.value = ''
                if (!file) return
                setErr(null)
                try {
                  const bytes = await avatarFromFile(file)
                  await run(() => setGroupIcon(g.gid, bytes))
                } catch (x) {
                  setErr(x instanceof Error ? x.message : 'That picture could not be used.')
                }
              }}
            />
          </label>
          {g.icon && (
            <button type="button" className="fr-btn" disabled={busy} onClick={() => void run(() => setGroupIcon(g.gid, null))}>
              Remove photo
            </button>
          )}
        </span>
      )}
      {err && <span className="grp-sub mch-err">{err}</span>}
    </div>
  )
}

/**
 * SOCIAL LINKS — owner, 2026-10-18: Group Owner 可以在 Group 的設定頁面裡面置入他所要的任何的 Social Link, 比如說
 * Facebook、Twitter 或是 Instagram, 或是 YouTube 之類的相關的連結, 讓其他的使用者在這一個設定頁面也能夠看得到.
 * Every member sees them, each by its site's name; the owner edits them (https only, at most eight — the BFF's rule).
 */
function SocialLinks({ g, owner, onEdit }: { g: GroupView; owner: boolean; onEdit: () => void }) {
  const social = g.social ?? []
  if (!social.length && !owner) return null
  return (
    <div className="grp-socials">
      <div className="gh-sec grp-h">Links</div>
      {social.length > 0 && (
        <div className="grp-social-row">
          {social.map((l) => (
            <a key={l.url} className="grp-social" href={l.url} target="_blank" rel="noopener noreferrer" title={l.url}>
              {l.site}
              <Icon name="open_in_new" size={13} />
            </a>
          ))}
        </div>
      )}
      {owner && (
        <button type="button" className="fr-btn grp-reset" onClick={onEdit}>
          {social.length ? 'Edit links' : 'Add links'}
        </button>
      )}
    </div>
  )
}

type RulesMode = 'read' | 'write'

/** The server's cap (groups.py RULES_MAX): the count turns red past it and Save waits. */
const RULES_MAX = 10000

/**
 * THE RULES — owner, 2026-10-18: 在 Group 的設定頁面, 讓使用者有一個設定按鈕, 可以彈出來讓使用者輸入 Group 的規則,
 * 上面可以使用 Markdown 的語法 … 公告規定在這個 group 裡面的使用者該如何利用這一個 group 論壇來一起討論以及哪些是
 * 違反規則的條款. An announcement: every member sees the top of it here, rendered, and reads the whole in its own
 * popup; the owner's button opens the same popup to write them. A member's sheet with no rules shows nothing.
 */
function GroupRules({ g, owner, onOpen }: { g: GroupView; owner: boolean; onOpen: (mode: RulesMode) => void }) {
  const rules = g.rules ?? ''
  const peek = useRef<HTMLDivElement>(null)
  // "Read all" only when the peek actually cut something off — a two-line rule needs no second screen.
  const [clipped, setClipped] = useState(false)
  useLayoutEffect(() => {
    const el = peek.current
    setClipped(!!el && el.scrollHeight > el.clientHeight + 1)
  }, [rules])
  if (!rules && !owner) return null
  return (
    <div className="grp-rules">
      <div className="gh-sec grp-h">Rules</div>
      {rules ? (
        <div ref={peek} className={`grp-rules-peek pmsg-body md${clipped ? ' clipped' : ''}`}>
          <Markdown body={rules} />
        </div>
      ) : (
        <span className="grp-sub grp-rules-none">
          Say how members should use this group, and what is not allowed.
        </span>
      )}
      <div className="grp-rules-btns">
        {rules && clipped && (
          <button type="button" className="fr-btn" onClick={() => onOpen('read')}>
            Read all rules
          </button>
        )}
        {owner && (
          <button type="button" className="fr-btn" onClick={() => onOpen('write')}>
            <Icon name="edit" size={14} />
            {rules ? 'Edit rules' : 'Add rules'}
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * The rules' own popup, over the group's sheet: the whole text for everyone; for the owner, Write / Preview —
 * Markdown typed, then seen exactly as members will see it — and Save. Saving it empty removes the rules.
 */
function RulesSheet({
  g,
  owner,
  mode,
  draft,
  busy,
  err,
  onMode,
  onDraft,
  onSave,
  onClose,
}: {
  g: GroupView
  owner: boolean
  mode: RulesMode
  draft: string
  busy: boolean
  err: string | null
  onMode: (mode: RulesMode) => void
  onDraft: (text: string | null) => void
  onSave: (text: string) => void
  onClose: () => void
}) {
  const rules = g.rules ?? ''
  const writing = owner && mode === 'write'
  const [preview, setPreview] = useState(false)
  const over = draft.length > RULES_MAX
  const changed = draft.trim() !== rules.trim()
  return (
    <Sheet
      title="Group rules"
      onClose={onClose}
      className="mch-sheet grp-sheet grp-rules-sheet"
      actions={
        writing ? (
          <>
            <button
              type="button"
              className="sheet-btn"
              onClick={() => {
                onDraft(null)
                if (rules) onMode('read')
                else onClose()
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="sheet-btn primary"
              disabled={busy || over || !changed}
              onClick={() => onSave(draft)}
            >
              {draft.trim() || !rules ? 'Save rules' : 'Remove rules'}
            </button>
          </>
        ) : owner ? (
          <button type="button" className="sheet-btn primary" onClick={() => onMode('write')}>
            Edit rules
          </button>
        ) : undefined
      }
    >
      {writing ? (
        <div className="grp-form grp-rules-form">
          <div className="vm-seg" role="radiogroup" aria-label="Write or preview">
            {(
              [
                [false, 'Write'],
                [true, 'Preview'],
              ] as [boolean, string][]
            ).map(([on, text]) => (
              <button
                key={text}
                type="button"
                role="radio"
                aria-checked={preview === on}
                className={`vm-seg-btn${preview === on ? ' on' : ''}`}
                onClick={() => setPreview(on)}
              >
                {text}
              </button>
            ))}
          </div>
          {preview ? (
            draft.trim() ? (
              <div className="grp-rules-full pmsg-body md">
                <Markdown body={draft} />
              </div>
            ) : (
              <p className="gh-note">Nothing written yet.</p>
            )
          ) : (
            <textarea
              className="gh-report grp-rules-input"
              value={draft}
              onChange={(e) => onDraft(e.target.value)}
              placeholder={'# How we talk here\n\n1. Be kind — no insults or harassment.\n2. Stay on topic.\n3. No spam or ads.'}
              rows={12}
              aria-label="Group rules"
              autoFocus
            />
          )}
          <span className="grp-sub grp-rules-hint">
            Markdown works: # heading, **bold**, *italic*, - list, 1. numbered, [link](https://…), &gt; quote. Every
            member can read them. Leave it empty to remove the rules.
          </span>
          <span className={`grp-sub grp-rules-count${over ? ' mch-err' : ''}`}>
            {draft.length.toLocaleString()} / {RULES_MAX.toLocaleString()}
          </span>
        </div>
      ) : rules ? (
        <div className="grp-rules-full pmsg-body md">
          <Markdown body={rules} />
        </div>
      ) : (
        <p className="gh-note">This group has no rules yet.</p>
      )}
      {err && writing && <p className="gh-note mch-err">{err}</p>}
    </Sheet>
  )
}

/** The owner's editor: one address per row — Facebook, X, Instagram, YouTube, or any https site. */
function EditLinks({ g, busy, onSave }: { g: GroupView; busy: boolean; onSave: (links: string[]) => void }) {
  const [rows, setRows] = useState<string[]>(() => [...(g.social ?? []).map((l) => l.url), ''])
  const set = (i: number, v: string) =>
    setRows((r) => {
      const next = r.map((x, j) => (j === i ? v : x))
      // A fresh empty row appears once the last is used, up to eight.
      return next[next.length - 1]?.trim() && next.length < 8 ? [...next, ''] : next
    })
  return (
    <form
      className="grp-form"
      onSubmit={(e) => {
        e.preventDefault()
        onSave(rows.map((r) => r.trim()).filter(Boolean))
      }}
    >
      <span className="grp-sub">Facebook, X, Instagram, YouTube — or any web address. Every member sees them.</span>
      {rows.map((r, i) => (
        <span className="grp-search" key={i}>
          <input
            className="nas-input"
            inputMode="url"
            placeholder="https://"
            aria-label={`Link ${i + 1}`}
            value={r}
            onChange={(e) => set(i, e.target.value)}
          />
          {r && (
            <button
              type="button"
              className="fr-btn"
              aria-label={`Remove link ${i + 1}`}
              onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}
            >
              <Icon name="close" size={14} />
            </button>
          )}
        </span>
      ))}
      <div className="svr-createrow svr-creatego">
        <button type="submit" className="sheet-btn primary" disabled={busy}>
          Save links
        </button>
      </div>
    </form>
  )
}

/**
 * THE LINK — owner, 2026-10-18: 使用者可以把看得到的 group 頁面直接使用 URL 的方式分享出去. Every member sees it
 * and passes it on (Copy, or the system's share sheet where there is one); anyone signed in who opens it may
 * join. The owner can Reset it — the old one stops working — for a link that went further than meant.
 */
function ShareLink({
  g,
  owner,
  busy,
  run,
}: {
  g: GroupView
  owner: boolean
  busy: boolean
  run: (fn: () => Promise<unknown>) => Promise<boolean>
}) {
  const [note, setNote] = useState<string | null>(null)
  const [armed, setArmed] = useState(false)
  if (!g.link) return null
  const url = groupLinkUrl(g.link)
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function'
  const copy = async () => {
    const r = await copyText(url)
    setNote(r.ok ? 'Link copied' : 'Could not copy — select the link and copy it')
  }
  const share = async () => {
    try {
      await navigator.share({ title: g.name, url })
    } catch (e) {
      if ((e as Error).name !== 'AbortError') void copy()
    }
  }
  return (
    <div className="grp-link">
      <div className="gh-sec grp-h">Invite link</div>
      <div className="grp-search">
        <input
          className="nas-input grp-link-url"
          readOnly
          value={url}
          aria-label="Group link"
          onFocus={(e) => e.target.select()}
        />
        <button type="button" className="fr-btn ok" onClick={() => void copy()}>
          Copy
        </button>
        {canShare && (
          <button type="button" className="fr-btn" onClick={() => void share()}>
            Share…
          </button>
        )}
      </div>
      <span className="grp-sub">Anyone signed in to Avengents who opens this link can join.</span>
      {owner &&
        (armed ? (
          <span className="fr-btns">
            <button
              type="button"
              className="fr-btn danger"
              disabled={busy}
              onClick={() => {
                setArmed(false)
                setNote(null)
                void run(() => resetGroupLink(g.gid))
              }}
            >
              Reset — the old link stops working
            </button>
            <button type="button" className="fr-btn" onClick={() => setArmed(false)}>
              Cancel
            </button>
          </span>
        ) : (
          <button type="button" className="fr-btn grp-reset" disabled={busy} onClick={() => setArmed(true)}>
            Reset link
          </button>
        ))}
      {note && (
        <span className="grp-sub" role="status">
          {note}
        </span>
      )}
    </div>
  )
}

/** The owner's half: who asked to join, who is still invited, and whether strangers can find it. */
function OwnerParts({
  g,
  busy,
  run,
}: {
  g: GroupView
  busy: boolean
  run: (fn: () => Promise<unknown>) => Promise<boolean>
}) {
  const asks = g.asks ?? []
  const invites = g.invites ?? []
  return (
    <>
      {asks.length > 0 && (
        <>
          <div className="gh-sec grp-h">Asking to join</div>
          {asks.map((a) => (
            <div className="grp-person" key={a.member}>
              <PersonFace person={a} />
              <span className="mch-meta">
                <span className="mch-name">{a.name ?? a.email}</span>
                {a.name && a.email && <span className="grp-sub">{a.email}</span>}
              </span>
              <span className="fr-btns">
                <button
                  type="button"
                  className="fr-btn ok"
                  disabled={busy}
                  onClick={() => void run(() => answerGroupAsk(g.gid, a.member, true))}
                >
                  Let in
                </button>
                <button
                  type="button"
                  className="fr-btn"
                  disabled={busy}
                  onClick={() => void run(() => answerGroupAsk(g.gid, a.member, false))}
                >
                  Decline
                </button>
              </span>
            </div>
          ))}
        </>
      )}
      {invites.length > 0 && (
        <>
          <div className="gh-sec grp-h">Invited</div>
          {invites.map((i) => (
            <div className="grp-person" key={i.member}>
              <PersonFace person={i} />
              <span className="mch-meta">
                <span className="mch-name">{i.name ?? i.email}</span>
                {i.name && i.email && <span className="grp-sub">{i.email}</span>}
              </span>
              <button
                type="button"
                className="fr-btn"
                disabled={busy}
                aria-label={`Take back the invitation to ${i.name ?? i.email ?? 'them'}`}
                onClick={() => void run(() => cancelGroupInvite(g.gid, i.member))}
              >
                Cancel
              </button>
            </div>
          ))}
        </>
      )}
      {/* 群組可以設定為隱藏或是可以被搜尋的 (owner, 2026-10-18). */}
      <label className="grp-toggle">
        <input
          type="checkbox"
          checked={g.visibility === 'searchable'}
          disabled={busy}
          onChange={(e) =>
            void run(() => updateGroup(g.gid, { visibility: e.target.checked ? 'searchable' : 'hidden' }))
          }
        />
        <span>
          <span className="grp-toggle-label">Searchable</span>
          <span className="grp-sub">
            Anyone can find this group by name and ask to join; you decide who gets in.
          </span>
        </span>
      </label>
    </>
  )
}

/** Invite by email — the same search ＋ → Humans uses, one card per account at the address. */
function InvitePeople({ gid, onInvited }: { gid: string; onInvited: () => void }) {
  const [email, setEmail] = useState('')
  const [found, setFound] = useState<{ email: string; accounts: FriendAccount[] } | null>(null)
  const [done, setDone] = useState<Record<string, 'invited' | 'joined'>>({})
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const search = async () => {
    const e = email.trim()
    if (!e) return
    setErr(null)
    setBusy(true)
    try {
      const r = await friendSearch(e)
      if (!r.found) {
        setFound(null)
        setErr(`Nobody has signed in here as “${e}”.`)
      } else setFound({ email: e, accounts: r.accounts ?? (r.account ? [r.account] : []) })
    } catch (x) {
      setErr(groupErrorText(x))
    } finally {
      setBusy(false)
    }
  }
  const invite = async (a: FriendAccount) => {
    if (!found) return
    setErr(null)
    setBusy(true)
    try {
      const r = await inviteToGroup(gid, found.email, a.account)
      setDone((d) => ({ ...d, [a.account ?? a.email]: r.status }))
      onInvited()
    } catch (x) {
      setErr(groupErrorText(x))
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="grp-invitebox">
      <form
        className="grp-search"
        onSubmit={(ev) => {
          ev.preventDefault()
          void search()
        }}
      >
        <input
          className="nas-input"
          type="email"
          inputMode="email"
          placeholder="Their email"
          aria-label="Their email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <button type="submit" className="sheet-btn" disabled={busy || !email.trim()}>
          Find
        </button>
      </form>
      {found?.accounts.map((a) => {
        const key = a.account ?? a.email
        const state = done[key]
        return (
          <div className="fr-card" key={key}>
            <Avatar picture={a.picture} label={a.name ?? a.email} email={a.email} className="fr-pic" />
            <span className="fr-meta">
              <span className="fr-name">
                {a.name ?? a.email}
                {a.provider && found.accounts.length > 1 ? (
                  <span className={`fr-prov ${a.provider}`}>{a.provider === 'apple' ? 'Apple' : 'Google'}</span>
                ) : null}
              </span>
              {a.name && <span className="fr-sub">{a.email}</span>}
            </span>
            {a.relationship === 'self' ? (
              <span className="fr-state self">This is you</span>
            ) : state ? (
              <span className="fr-state friends">{state === 'joined' ? 'Joined ✓' : 'Invited ✓'}</span>
            ) : (
              <button type="button" className="fr-btn ok" disabled={busy} onClick={() => void invite(a)}>
                Invite
              </button>
            )}
          </div>
        )
      })}
      {err && <p className="gh-note mch-err">{err}</p>}
    </div>
  )
}

/**
 * Leaving. An owner may name the next owner — 「如果他不指定 Group Owner 的話, 那第二位加入 Group 的人就
 * 自動順位變成 Group Owner」: the default is whoever joined earliest of those staying. The last one out
 * ends the group, and the screen says so before the tap.
 */
function LeaveGroup({ g, busy, onLeave }: { g: GroupView; busy: boolean; onLeave: (next?: string) => void }) {
  const others = g.members.filter((m) => !m.you)
  const [next, setNext] = useState<string>('')
  const [armed, setArmed] = useState(false)
  const owner = g.role === 'owner'
  const firstName = (p: GroupPerson | undefined) => p?.name ?? p?.email ?? 'the next member'
  return (
    <div className="grp-leave">
      {others.length === 0 ? (
        <p className="gh-note">You are the only one here — leaving ends the group.</p>
      ) : owner ? (
        <>
          <p className="gh-note">Who looks after the group when you go?</p>
          <label className="grp-pick">
            <input type="radio" name="next" checked={next === ''} onChange={() => setNext('')} />
            <span>{firstName(others[0])} — joined first</span>
          </label>
          {others.slice(1).map((m) => (
            <label className="grp-pick" key={m.member}>
              <input type="radio" name="next" checked={next === m.member} onChange={() => setNext(m.member)} />
              <span>{firstName(m)}</span>
            </label>
          ))}
        </>
      ) : (
        <p className="gh-note">You can come back only if you are invited again{g.visibility === 'searchable' ? ' or let in' : ''}.</p>
      )}
      {armed ? (
        <span className="fr-btns">
          <button type="button" className="fr-btn danger" disabled={busy} onClick={() => onLeave(next || undefined)}>
            {others.length === 0 ? 'End the group' : `Leave ${g.name}?`}
          </button>
          <button type="button" className="fr-btn" onClick={() => setArmed(false)}>
            Cancel
          </button>
        </span>
      ) : (
        <button type="button" className="sheet-btn primary grp-leave-go" disabled={busy} onClick={() => setArmed(true)}>
          Leave group
        </button>
      )}
    </div>
  )
}
